import { ComponentType } from "react";

import { Separator } from "@/components/ui/separator";
import { CountingNumber } from "@/components/ui/shadcn-io/counting-number";
import { ModuleStatsHero } from "@/lib/types";
import { cn } from "@/lib/ui/cn";

type CardProps = {
  card: ModuleStatsHero["cards"][number];
  isActive?: boolean;
  Icon: ComponentType<React.SVGProps<SVGSVGElement>>;
};

export function Card({ card, isActive, Icon }: CardProps) {
  return (
    <div
      key={card._key}
      className={cn(
        "p-padding-element-xl desktop:size-full group/item relative flex size-full h-[280px] w-[280px] shrink-0 flex-col justify-center overflow-hidden rounded-lg backdrop-blur-[44px] transition-all",
        "bg-[linear-gradient(180deg,rgba(189,212,252,0.20)_0%,rgba(178,178,179,0.20)_100%)] bg-blend-soft-light outline -outline-offset-1 outline-[rgba(151,193,255,0.40)]",
        isActive
          ? "desktop:col-span-3 desktop:row-span-4 gap-gap-element-lg desktop:group-hover/root:before:opacity-0 desktop:group-hover/root:shadow-[inset_0px_-2px_1px_0px_#97C1FF66,0px_11.39px_22.34px_0px_#356AB917] tablet:shadow-[0px_3.71px_24.85px_0px_#00E58D0E,0px_10.27px_23.4px_0px_#00E58D1F,0px_24.72px_32.26px_0px_#2DF7A920] before:opacity-100"
          : "gap-gap-element-md desktop:col-span-2 desktop:row-span-2 shadow-[inset_0px_-2px_1px_0px_#97C1FF66,0px_11.39px_22.34px_0px_#356AB917]",
        "before:bg-blend-linear-dodge before:absolute before:inset-0 before:rounded-lg before:bg-[radial-gradient(ellipse_119.68%_208.53%_at_27.93%_100%,var(--color-emerald-lighter)_28%,var(--color-emerald-light)_77%,var(--color-emerald)_100%)] before:transition-opacity",
        isActive ? "before:opacity-100" : "before:opacity-0",
        "before:-z-10",
        "desktop:hover:before:opacity-100 desktop:hover:shadow-[0px_3.71px_24.85px_0px_#00E58D0E,0px_10.27px_23.4px_0px_#00E58D1F,0px_24.72px_32.26px_0px_#2DF7A920]",
      )}
    >
      <Icon
        className={cn(
          "text-indigo-lighter size-12 transition-colors",
          isActive &&
            "text-emerald-darkest desktop:size-20 desktop:group-hover/root:text-indigo-lighter",
          "desktop:group-hover/item:text-emerald-darkest!",
        )}
      />
      <div
        className={cn(
          "gap-gap-element-md text-indigo-lighter relative z-10 flex flex-col transition-colors",
          isActive && "text-emerald-darkest desktop:group-hover/root:text-indigo-lighter",
          "desktop:group-hover/item:text-emerald-darkest!",
        )}
      >
        <div className={cn("typography-heading-3", isActive && "desktop:typography-heading-1")}>
          <span className="sr-only">{card.title}</span>
          <span aria-hidden="true">
            {(() => {
              const text = card.title || "";
              const parts = text.split(/(\d+)/);

              return parts.map((part, index) => {
                const isNumeric = /^\d+$/.test(part);
                if (isNumeric) {
                  return <CountingNumber inView key={index} number={+part} />;
                }
                return <span key={index}>{part}</span>;
              });
            })()}
          </span>
        </div>
        <Separator
          className={cn(
            "bg-indigo-lighter transition-colors",
            isActive && "bg-emerald-darkest desktop:group-hover/root:bg-indigo-lighter",
            "desktop:group-hover/item:bg-emerald-darkest!",
          )}
        />
        <div
          className={cn(
            "typography-text-medium-medium",
            isActive && "desktop:typography-heading-6",
          )}
        >
          {card.body}
        </div>
      </div>
    </div>
  );
}
